import { validate } from 'uuid';

import type { Action, MetaAction } from 'modules/global/generic.types';
import type { MatcherFunction } from 'modules/global/reducers/reducers.types';
import { isFailureRequest, isStartRequest, isSuccessRequest, readEntityId } from 'modules/global/reducers/utils';
import type { MayBe } from 'modules/global/typing/common/common';
import { createReducer } from 'utils/ReduxUtils';

export type LoadingArraySlice = Array<string>;

/**
 * Reads the id of the entity a request is about, from its payload and the action meta.
 * Modules provide it when the id lives neither in a bare uuid payload nor in `payload.id` —
 * e.g. `payload.cameraId` on the request action.
 * @template E The payload shapes the module dispatches for this action root
 */
export type SelectLoadingId<E> = (entity: E | undefined, meta: MetaAction) => MayBe<string>;

export type LoadingArraySliceConfig<E> = {
  matcher?: MatcherFunction;
  selectId?: SelectLoadingId<E>;
};

const initialState: LoadingArraySlice = [];

// The success/failure payloads are NOT the `E` the module describes — they are whatever the API
// returned or the error thrown — so they are read defensively, not parameterized.
const readId = readEntityId;

// Failure callbacks tag the entity a failed request was about on `meta.entityId` — their
// payload is the raw API error and carries no id.
const readEntityIdFromMeta = (meta: MayBe<MetaAction>): MayBe<string> =>
  (typeof meta?.entityId === 'string' ? meta.entityId : undefined);

// A payload the module cannot map to an id is not tracked — the slice stays a list of ids.
const addOne = (state: LoadingArraySlice, entityId: MayBe<string>): LoadingArraySlice =>
  (entityId ? [...state, entityId] : state);

const removeOne = (state: LoadingArraySlice, entityId: MayBe<string>): LoadingArraySlice =>
  state.filter(id => id !== entityId);

const loadingArraySlice = <E = unknown>(
  actionRoot: string,
  { global: config }: { global: LoadingArraySliceConfig<E> }
) => {
  const selectId = ({ payload, meta }: Action<E>): MayBe<string> => {
    if (typeof payload === 'string' && validate(payload)) {
      return payload;
    }
    return config.selectId?.(payload, meta ?? {}) ?? readEntityIdFromMeta(meta) ?? readId(payload);
  };

  return createReducer(initialState, {}, [
    {
      matcher: isStartRequest(actionRoot, config),
      reducer: (state, action) => addOne(state, selectId(action)),
    },
    {
      matcher: isSuccessRequest(actionRoot, config),
      reducer: (state, action) => removeOne(state, selectId(action)),
    },
    {
      matcher: isFailureRequest(actionRoot, config),
      reducer: (state, action) => removeOne(state, selectId(action)),
    },
  ]);
};

export default loadingArraySlice;
